import type { ActionView } from './action-confirmation';
import { copyForTool, type ActionCopy } from './action-copy';
import type { ActionSurfaceCopy, ThemeChoice } from './action-surface-copy';
import { displayFields, readableKey } from './action-approval-copy';
import { namespaceCopyFor } from '@/i18n/ns';
import actionPrimaryCopy from '@/i18n/ns/action-primary';

function settingLabel(
  key: string,
  value: unknown,
  receipt: boolean,
  copy: ActionSurfaceCopy,
  locale: string,
) {
  const text = value == null ? (key === 'theme' ? 'system' : '—') : String(value);
  if (key === 'theme' && ['dark', 'light', 'system'].includes(text))
    return (receipt ? copy.receiptTheme : copy.pendingTheme)[text as ThemeChoice];
  if (key === 'language') {
    let name = text;
    try {
      name = new Intl.DisplayNames([locale], { type: 'language' }).of(text) ?? text;
    } catch {
      /* Retain exact value. */
    }
    return (receipt ? copy.languageReceipt : copy.languagePending)(name);
  }
  if (key === 'timezone') return (receipt ? copy.timezoneReceipt : copy.timezonePending)(text);
  if (key === 'chat_model_choice') return (receipt ? copy.modelReceipt : copy.modelPending)(text);
  return null;
}

/** Only fixed tool/field identities select copy; model prose never determines routing. */
export function compactActionLabel(
  action: ActionView,
  copy: ActionSurfaceCopy,
  shared: ActionCopy,
  locale: string,
  receipt = false,
  summary = shared.review,
) {
  const fields = copyForTool(shared, action.tool);
  const entries = displayFields(action, fields);
  if (action.tool === 'update_my_settings')
    return entries
      .map(
        ([key, value]) =>
          settingLabel(key, value, receipt, copy, locale) ??
          `${readableKey(key, fields)}: ${String(value)}`,
      )
      .join(' · ');
  if (action.tool === 'save_memory' && typeof action.args.content === 'string')
    return (receipt ? copy.rememberReceipt : copy.rememberPending)(action.args.content);
  if (action.tool === 'forget_memory' && typeof action.args.content === 'string')
    return (receipt ? copy.forgetReceipt : copy.forgetPending)(action.args.content);
  // The summary is already the whole sentence (the App, what it may do and where).
  if (
    [
      'approve_app_outside_access',
      'set_app_event',
      'set_app_schedule',
      'set_app_data_writes',
      'apply_app_table_change',
      'set_app_connected_write',
      'confirm_app_code',
      'run_app_function_with_changes',
    ].includes(action.tool)
  )
    return summary;
  return `${summary}${entries.length ? ` · ${entries.map(([key, value]) => `${readableKey(key, fields)}: ${String(value)}`).join(' · ')}` : ''}`;
}

export function actionPrimaryLabel(
  action: ActionView,
  copy: ActionSurfaceCopy,
  fallback: string,
  locale = 'en',
) {
  const labels = namespaceCopyFor(actionPrimaryCopy, locale);
  if (action.tool === 'create_agent')
    return action.args.draft === true
      ? labels.saveDraft
      : action.args.schedule && action.args.enabled
        ? labels.createAndEnable
        : labels.createAgent;
  if (action.tool === 'update_agent') return labels.applyAgentChanges;
  if (action.tool === 'set_agent_trigger')
    return action.args.schedule && action.args.enabled ? labels.enableSchedule : labels.saveTrigger;
  if (action.tool === 'run_agent') return labels.runAgent;
  if (action.tool === 'update_project') return labels.applyProjectChanges;
  // What the person approves when an editor changes what shared work runs on.
  if (action.tool === 'update_work_connection')
    return action.args.change === 'remove'
      ? labels.removeConnection
      : action.args.change === 'take_over'
        ? labels.useMyAccess
        : labels.connect;
  const names: Record<string, keyof ActionSurfaceCopy['primaryVerbs']> = {
    create_skill_draft: 'createSkill',
    update_skill_draft: 'updateSkill',
    create_agent: 'createAgent',
    save_work_result: 'saveResult',
    propose_company_knowledge: 'suggestKnowledge',
    propose_knowledge_change: 'suggestPage',
    build_artifact: 'buildDocument',
    suggest_page_edits: 'suggestPage',
  };
  return names[action.tool] ? copy.primaryVerbs[names[action.tool]] : fallback;
}

export function artifactActionLabel(action: ActionView, copy: ActionSurfaceCopy, locale = 'en') {
  const kinds: Record<string, keyof ActionSurfaceCopy['artifact']> = {
    create_skill_draft: 'skill',
    update_skill_draft: 'skill',
    create_agent: 'agent',
    save_work_result: 'result',
    propose_company_knowledge: 'knowledge',
    propose_knowledge_change: 'knowledge',
    suggest_page_edits: 'knowledge',
  };
  const kind = kinds[action.tool];
  // A build's chip never says "Approval needed" once the request is decided: waiting, expired,
  // or (executed, rejected, failed) the action's own label. The live job state is the card's to add.
  if (action.tool === 'build_artifact') {
    if (action.status === 'pending') return copy.approvalWaiting;
    if (action.status === 'expired') return copy.approvalExpired;
    return actionPrimaryLabel(action, copy, copy.approvalNeeded, locale);
  }
  if (!kind) return copy.approvalNeeded;
  // The leading label follows the live action status, never the tool part it began as: saved,
  // waiting for approval, or expired. Any other outcome names the action.
  if (action.status === 'executed') return copy.artifact[kind].saved;
  if (action.status === 'pending') return copy.approvalWaiting;
  if (action.status === 'expired') return copy.approvalExpired;
  return actionPrimaryLabel(action, copy, copy.approvalNeeded, locale);
}
